/**
 * Downscale photos in the browser before upload.
 * Site photos come straight from phone cameras; we keep a follow-up original
 * (long edge 2560, JPEG 0.82) instead of a huge camera file or a q=1 1080p re-encode.
 */

export const ALLOWED_PHOTO_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/heic",
];

export const MAX_LONG_EDGE = 2560;
export const JPEG_QUALITY = 0.82;

/**
 * Fit an image into maxLongEdge on its longest side, preserving aspect ratio.
 * Does not upscale.
 */
export function targetSize(width, height, maxLongEdge = MAX_LONG_EDGE) {
  const longEdge = Math.max(width, height);
  if (!width || !height || longEdge <= maxLongEdge) {
    return { width, height };
  }
  const scale = maxLongEdge / longEdge;
  return {
    width: Math.round(width * scale),
    height: Math.round(height * scale),
  };
}

/** Resize a single image file to the follow-up original, returning a JPEG File. */
export async function resizeImageFile(file) {
  const img = document.createElement("img");
  const reader = new FileReader();

  await new Promise((resolve, reject) => {
    reader.onload = (event) => {
      img.src = event.target.result;
      img.onload = resolve;
      img.onerror = reject;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const { width, height } = targetSize(img.width, img.height);

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0, width, height);

  const blob = await new Promise((resolve, reject) => {
    canvas.toBlob(
      (result) => {
        if (result) resolve(result);
        else reject(new Error("Failed to create Blob"));
      },
      "image/jpeg",
      JPEG_QUALITY
    );
  });

  return new File([blob], file.name, { type: "image/jpeg" });
}

/** Resize a list of files, skipping any that fail rather than aborting the batch. */
export async function resizeImageFiles(files, onError) {
  const resized = [];
  for (const file of files) {
    try {
      resized.push(await resizeImageFile(file));
    } catch (error) {
      onError?.(error, file);
    }
  }
  return resized;
}
